// =============================================
// CTFD "Success Tax" Quiz — Pure logic module
// (questions, scoring, derived metrics)
// =============================================

export const QUESTIONS = [
  { text: 'How many hours do you typically work in a week?', answers: ['Under 40','40\u201349','50\u201359','60\u201369','70+'], scores: [0,2,5,8,10] },
  { text: 'How many meetings or calls do you typically have on a weekday?', answers: ['0\u20132','3\u20134','5\u20136','7\u20138','9+'], scores: [0,2,5,8,10] },
  { text: 'How often do you feel behind, or like there isn\u2019t enough time to finish what matters in a day?', answers: ['Never','Rarely','Sometimes','Often','Almost always'], scores: [0,2,5,8,10] },
  { text: 'Roughly how many people depend on your leadership rhythm in a meaningful way?', helper: 'Employees, contractors, direct reports, assistants, collaborators, managers, or other people whose pace is meaningfully affected by yours', type: 'numeric', placeholder: 'e.g. 12' },
  { text: 'What is your average daily screen time?', answers: ['Under 3 hours','3\u20135 hours','5\u20137 hours','7\u20139 hours','9+ hours'], scores: [0,2,5,8,10] },
  { text: 'About how many times do you pick up your phone in a day?', answers: ['Under 50','50\u201399','100\u2013149','150\u2013199','200+'], scores: [0,2,5,8,10] },
  { text: 'How often do you check your phone or laptop first thing in the morning before checking in with yourself?', answers: ['Never','Rarely','Sometimes','Often','Almost always'], scores: [0,2,5,8,10] },
  { text: 'How often do you feel pressure to respond as soon as someone messages you?', answers: ['Never','Rarely','Sometimes','Often','Almost always'], scores: [0,2,5,8,10] },
  { text: 'How many hours per week do you intentionally spend downregulating?', helper: 'Walking without input, meditation, breathwork, stillness, prayer, journaling, device-free decompression, intentional rest', answers: ['5+ hours','3\u20135 hours','1\u20132 hours','Less than 1 hour','0'], scores: [0,2,5,8,10] },
  { text: 'How many uninterrupted 90-minute focus blocks do you get in a typical week?', answers: ['8+','5\u20137','3\u20134','1\u20132','0'], scores: [0,2,5,8,10] },
  { text: 'Do you have actual offline boundaries, or are you basically always available to your business?', answers: ['Yes, I have real offline boundaries','No, I\u2019m basically always available'], scores: [0,10] },
  { text: 'If a work obligation conflicts with a personal obligation, how likely are you to cancel the personal one?', answers: ['Very unlikely','Unlikely','Depends','Likely','Very likely'], scores: [0,2,5,8,10] },
  { text: 'If you\u2019re sick, how likely are you to still work instead of fully resting and recovering?', answers: ['Very unlikely','Unlikely','Depends','Likely','Very likely'], scores: [0,2,5,8,10] },
  { text: 'How often do you feel guilty taking a break or being on vacation?', answers: ['Never','Rarely','Sometimes','Often','Almost always'], scores: [0,2,5,8,10] },
  { text: 'How often do you catch yourself moving faster than the moment actually requires?', helper: 'Walking fast, talking fast, eating fast, rushing between small tasks', answers: ['Never','Rarely','Sometimes','Often','Almost always'], scores: [0,2,5,8,10] },
];

// Normalize numeric downstream count to 0/2/4/7/10 score for Leadership Spillover
export function normalizeDownstreamScore(count) {
  const n = Number(count) || 0;
  if (n <= 0) return 0;
  if (n <= 10) return 2;
  if (n <= 25) return 4;
  if (n <= 50) return 7;
  return 10;
}

export function computeResults(answers) {
  const r = QUESTIONS.map((q, i) => {
    if (q.type === 'numeric') return normalizeDownstreamScore(answers[i]);
    return q.scores[answers[i]] || 0;
  });

  const load = Math.round(((r[0]*0.4118)+(r[1]*0.2353)+(r[2]*0.3529))*10);
  const recoveryGap = Math.round(((r[8]*0.35)+(r[12]*0.20)+(r[10]*0.15)+(r[14]*0.15)+(r[0]*0.15))*10);
  const fragmentation = Math.round(((r[1]*0.20)+(r[4]*0.15)+(r[5]*0.25)+(r[6]*0.15)+(r[9]*0.25))*10);
  const boundaryCollapse = Math.round(((r[10]*0.25)+(r[11]*0.20)+(r[12]*0.20)+(r[13]*0.20)+(r[7]*0.15))*10);
  const leadershipSpillover = Math.round(((r[3]*0.40)+(r[10]*0.20)+(r[11]*0.15)+(r[12]*0.15)+(r[7]*0.10))*10);

  const overall = Math.round((load*0.20)+(recoveryGap*0.25)+(fragmentation*0.20)+(boundaryCollapse*0.20)+(leadershipSpillover*0.15));

  const sectionLabel = (s) => s <= 24 ? 'Contained' : s <= 49 ? 'Compounding' : s <= 74 ? 'Expensive' : 'Critical';
  const overallBand = overall <= 24 ? 'Low Success Tax' : overall <= 44 ? 'Rising Success Tax' : overall <= 64 ? 'Heavy Success Tax' : overall <= 79 ? 'Severe Success Tax' : 'Unsustainable Success Tax';

  // Archetype: 5-pattern system. Leadership Spillover is the downstream modifier, NOT an archetype.
  // Candidates exclude leadershipSpillover.
  const archetypeCandidates = { load, recoveryGap, fragmentation, boundaryCollapse };
  const maxCandidate = Math.max(load, recoveryGap, fragmentation, boundaryCollapse);

  let archetype;
  if (overall <= 30 && maxCandidate < 50) {
    archetype = 'Grounded Operator';
  } else {
    let dominant = 'load';
    for (const [k, v] of Object.entries(archetypeCandidates)) {
      if (v > archetypeCandidates[dominant]) dominant = k;
    }
    if (dominant === 'load') archetype = 'Loaded Leader';
    else if (dominant === 'fragmentation') archetype = 'Fractured Achiever';
    else if (dominant === 'boundaryCollapse') archetype = 'Boundaryless Performer';
    else if (dominant === 'recoveryGap') archetype = 'Overclocked Executive';
    else archetype = 'Loaded Leader';
  }

  // Downstream Impact modifier (independent of archetype). Driven by Leadership Spillover score.
  let downstreamImpact;
  if (leadershipSpillover <= 24) downstreamImpact = 'Low';
  else if (leadershipSpillover <= 49) downstreamImpact = 'Moderate';
  else downstreamImpact = 'High';

  return {
    rawScores: r,
    rawAnswers: QUESTIONS.map((q, i) => {
      if (q.type === 'numeric') {
        const n = Number(answers[i]) || 0;
        return `${n} ${n === 1 ? 'person' : 'people'}`;
      }
      return q.answers[answers[i]];
    }),
    sectionScores: { load, recoveryGap, fragmentation, boundaryCollapse, leadershipSpillover },
    sectionLabels: { load: sectionLabel(load), recoveryGap: sectionLabel(recoveryGap), fragmentation: sectionLabel(fragmentation), boundaryCollapse: sectionLabel(boundaryCollapse), leadershipSpillover: sectionLabel(leadershipSpillover) },
    overallScore: overall,
    overallBand,
    archetype,
    downstreamImpact,
  };
}

const DRIVER_MAP = [
  { qi: 0, threshold: 8, text: (a) => `You reported working ${a} hours per week` },
  { qi: 1, threshold: 8, text: (a) => `You have ${a} meetings or calls on a typical weekday` },
  { qi: 2, threshold: 8, text: (a) => `You ${a.toLowerCase()} feel behind or like there isn\u2019t enough time` },
  { qi: 3, threshold: 7, text: (a) => `${a} depend on your leadership rhythm` },
  { qi: 4, threshold: 8, text: (a) => `You reported ${a.toLowerCase()} of daily screen time` },
  { qi: 5, threshold: 8, text: (a) => `You pick up your phone ${a.toLowerCase()} times a day` },
  { qi: 6, threshold: 8, text: (a) => `You ${a.toLowerCase()} check your phone before checking in with yourself` },
  { qi: 7, threshold: 8, text: (a) => `You ${a.toLowerCase()} feel pressure to respond immediately` },
  { qi: 8, threshold: 8, text: (a) => `You spend ${a.toLowerCase()} per week on real recovery` },
  { qi: 9, threshold: 8, text: (a) => `You get ${a.toLowerCase()} uninterrupted focus blocks per week` },
  { qi: 10, threshold: 10, text: () => `You are basically always available to your business` },
  { qi: 11, threshold: 8, text: (a) => `You said you would ${a === 'Very likely' ? 'very likely' : 'likely'} cancel personal plans for work` },
  { qi: 12, threshold: 8, text: (a) => `You said you would ${a === 'Very likely' ? 'very likely' : 'likely'} still work while sick` },
  { qi: 13, threshold: 8, text: (a) => `You ${a.toLowerCase()} feel guilty taking a break or vacation` },
  { qi: 14, threshold: 8, text: (a) => `You ${a.toLowerCase()} catch yourself moving faster than the moment requires` },
];

export function computeTopDrivers(rawScores, rawAnswers) {
  return DRIVER_MAP
    .filter(d => rawScores[d.qi] >= d.threshold)
    .sort((a, b) => rawScores[b.qi] - rawScores[a.qi])
    .slice(0, 4)
    .map(d => d.text(rawAnswers[d.qi]));
}

export function computeRecoveryDebt(answerIndices) {
  const workMidpoints = [35, 45, 55, 65, 75];
  const recoveryMidpoints = [5.5, 4, 1.5, 0.5, 0];
  const workHours = workMidpoints[answerIndices[0]] || 35;
  const reportedRecovery = recoveryMidpoints[answerIndices[8]] || 0;
  const requiredRecovery = workHours * 0.28;
  const debt = Math.max(0, requiredRecovery - reportedRecovery);
  return {
    workHours,
    reportedRecovery,
    requiredRecovery: Math.round(requiredRecovery * 10) / 10,
    debt: Math.round(debt * 10) / 10,
    dailyActive: Math.round(requiredRecovery * 0.25 * 10) / 10,
    weeklyDeep: Math.round(requiredRecovery * 0.75 * 10) / 10,
    dailyAvg: Math.round((requiredRecovery / 7) * 10) / 10,
  };
}

export function computeAttentionLeak(answerIndices) {
  const meetingMid = [1, 3.5, 5.5, 7.5, 9.5];
  const phoneMid = [25, 75, 125, 175, 225];
  const morningPen = [0, 5, 10, 15, 20];
  const focusPen = [0, 30, 60, 120, 180];

  const meetingLeak = (meetingMid[answerIndices[1]] || 1) * 10 * 5;
  const pickupLeak = (phoneMid[answerIndices[5]] || 25) * 0.10 * 2 * 7;
  const morningLeak = (morningPen[answerIndices[6]] || 0) * 5;
  const focusLeak = focusPen[answerIndices[9]] || 0;

  const totalMin = meetingLeak + pickupLeak + morningLeak + focusLeak;
  const totalHrs = Math.round((totalMin / 60) * 10) / 10;

  let displayRange;
  if (totalHrs <= 3) displayRange = '1\u20133 hrs/week';
  else if (totalHrs <= 6) displayRange = '3\u20136 hrs/week';
  else if (totalHrs <= 10) displayRange = '6\u201310 hrs/week';
  else displayRange = '10\u201315+ hrs/week';

  return { totalMinutes: totalMin, totalHours: totalHrs, displayRange };
}

export function attentionLeakMidpoint(displayRange) {
  if (displayRange === '1\u20133 hrs/week') return 2;
  if (displayRange === '3\u20136 hrs/week') return 4.5;
  if (displayRange === '6\u201310 hrs/week') return 8;
  if (displayRange === '10\u201315+ hrs/week') return 12.5;
  return 2;
}

export function computeTimeReclaimed(displayRange) {
  let low, high, range;
  if (displayRange === '1\u20133 hrs/week') { low = 1; high = 2; range = '1\u20132 hrs/week'; }
  else if (displayRange === '3\u20136 hrs/week') { low = 2; high = 3; range = '2\u20133 hrs/week'; }
  else if (displayRange === '6\u201310 hrs/week') { low = 3; high = 5; range = '3\u20135 hrs/week'; }
  else if (displayRange === '10\u201315+ hrs/week') { low = 5; high = 8; range = '5\u20138 hrs/week'; }
  else {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn(`[quizLogic] computeTimeReclaimed: unknown displayRange "${displayRange}", defaulting to 1-2 hrs/week`);
    }
    low = 1; high = 2; range = '1\u20132 hrs/week';
  }
  return {
    low,
    high,
    range,
    monthlyLow: Math.round(low * 4.33),
    monthlyHigh: Math.round(high * 4.33),
    yearlyLow: Math.round(low * 52),
    yearlyHigh: Math.round(high * 52),
    yearlyDaysLow: Math.round((low * 52) / 24),
    yearlyDaysHigh: Math.round((high * 52) / 24),
    yearlyWorkdaysLow: Math.round((low * 52) / 8),
    yearlyWorkdaysHigh: Math.round((high * 52) / 8),
  };
}

export function computeLeanWorkweek(answerIndices, timeReclaimed) {
  const workMidpoints = [35, 45, 55, 65, 75];
  const workHours = workMidpoints[answerIndices[0]] || 35;
  const leanLow = workHours - timeReclaimed.high;
  const leanHigh = workHours - timeReclaimed.low;
  return { workHours, leanLow, leanHigh };
}

export function computeTeamSpillover(answers, leadershipLabel, attentionLeakDisplayRange) {
  const downstreamCount = Number(answers[3]) || 0;
  const factorMap = { 'Contained': 0.10, 'Compounding': 0.20, 'Expensive': 0.30, 'Critical': 0.40 };
  const spilloverFactor = factorMap[leadershipLabel] ?? 0.10;
  const leakMid = attentionLeakMidpoint(attentionLeakDisplayRange);
  const teamSpilloverHours = Math.round(downstreamCount * leakMid * spilloverFactor);
  return { downstreamCount, leakMid, spilloverFactor, label: leadershipLabel, teamSpilloverHours };
}


// =============================================
// ARCHETYPE METADATA (default copy — AI may override)
// =============================================
export const ARCHETYPES = {
  'Grounded Operator': {
    tagline: 'Low tax, mostly contained',
    diagnosis: "You're running a relatively contained operation — the system isn't taxing you the way it taxes most leaders.",
    summary: "Most of your patterns are working. There's no single area that's quietly costing you. The job from here is to protect what's already working, so it doesn't drift as the load grows.",
  },
  'Loaded Leader': {
    tagline: 'Volume / responsibility overload',
    diagnosis: "You're carrying a lot, and the main tax is how much responsibility you're holding.",
    summary: "The cost isn't in your habits — it's in the sheer volume. Too much is on your plate for any system to absorb cleanly. Until the load is shaped, recovery and focus stay squeezed.",
  },
  'Fractured Achiever': {
    tagline: 'Fragmentation / attention leak',
    diagnosis: "Your main cost isn't effort — it's that your attention is scattered across too many inputs.",
    summary: "You're producing, but in pieces. The constant switching is silently shrinking your depth and your recovery. The problem isn't the work — it's how often it gets interrupted.",
  },
  'Boundaryless Performer': {
    tagline: 'Weak edges / work bleeding everywhere',
    diagnosis: "Your system is running hot because work has stopped having clean edges.",
    summary: "You're too available. Work is bleeding into evenings, weekends, sick days, and personal time. The pace can look productive from the outside while quietly eroding the parts of life that recharge you.",
  },
  'Overclocked Executive': {
    tagline: 'Chronic pressure + under-recovery',
    diagnosis: "Your system is running hot — too much pressure, not enough recovery.",
    summary: "You're operating at high RPM with the recovery margin of a much smaller load. That gap doesn't show up immediately — it shows up as creeping fatigue, shorter fuse, and reduced clarity over time.",
  },
};
